import React, { useRef, useState } from "react";
import { Trees, ShieldHalf, LogIn, HelpCircle, Send } from "lucide-react";

const viewTexts = {
  login: { title: "Giriş Yapın", desc: "Uygulamaya erişmek için uygulama şifrenizi girin." },
  "forgot-1": { title: "Şifre Sıfırlama", desc: "Sisteme kayıtlı telefon numaranızı girin." },
  "forgot-2": { title: "Doğrulama", desc: "Telefonunuza gönderilen kodu girin." },
  "forgot-3": { title: "Yeni Şifre", desc: "Yeni uygulamanız için şifrenizi belirleyin." },
};

const inputBase =
  "w-full bg-slate-900 border border-slate-700 focus:border-brand-500 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none transition-colors text-center font-bold";

const submitBase =
  "w-full bg-brand-600 hover:bg-brand-500 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-brand-600/30 transition-colors mt-2 active:scale-95 text-sm flex items-center justify-center gap-2";

export default function AuthScreen({ authView, onSwitchView, onLogin, verifyPhone, onPasswordReset, notify }) {
  const otpRef = useRef("");
  const [loginPass, setLoginPass] = useState("");
  const [forgotPhone, setForgotPhone] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotPass, setForgotPass] = useState("");
  const [forgotPassConfirm, setForgotPassConfirm] = useState("");

  const texts = viewTexts[authView] || viewTexts.login;

  const handleLogin = (e) => {
    e.preventDefault();
    if (onLogin(loginPass)) {
      setLoginPass("");
    } else {
      notify("Hata", "Hatalı şifre girdiniz. Lütfen tekrar deneyin.", "error", false);
      setLoginPass("");
    }
  };

  const handleForgotStep1 = (e) => {
    e.preventDefault();
    if (verifyPhone(forgotPhone.trim())) {
      otpRef.current = Math.floor(100000 + Math.random() * 900000).toString();
      notify(
        "SMS Gönderildi",
        `(Simülasyon)\nSisteme kayıtlı numaranıza gönderilen doğrulama kodu:\n\n${otpRef.current}`,
        "info",
        false
      );
      onSwitchView("forgot-2");
    } else {
      notify("Bulunamadı", "Girdiğiniz telefon numarası sistemdeki numarayla eşleşmedi.", "error", false);
    }
  };

  const handleForgotStep2 = (e) => {
    e.preventDefault();
    if (forgotOtp.trim() === otpRef.current) {
      notify("Doğrulandı", "Kod doğru. Şimdi yeni şifrenizi belirleyebilirsiniz.", "success", false);
      onSwitchView("forgot-3");
    } else {
      notify("Hata", "Girdiğiniz doğrulama kodu hatalı.", "error", false);
    }
  };

  const handleForgotStep3 = (e) => {
    e.preventDefault();
    if (forgotPass !== forgotPassConfirm) {
      notify("Hata", "Girdiğiniz şifreler uyuşmuyor.", "error", false);
      return;
    }
    if (forgotPass.length < 4) {
      notify("Uyarı", "Şifreniz en az 4 karakter olmalıdır.", "info", false);
      return;
    }
    onPasswordReset(forgotPass);
    setForgotPass("");
    setForgotPassConfirm("");
    setForgotOtp("");
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 w-full max-w-sm rounded-3xl p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute -right-10 -top-10 text-brand-500/10 pointer-events-none">
          <ShieldHalf size={150} strokeWidth={1} />
        </div>

        <div className="text-center relative z-10 mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-brand-500/20 text-brand-400 mb-4 border border-brand-500/30 shadow-[0_0_20px_rgba(34,197,94,0.3)]">
            <Trees size={38} />
          </div>
          <h2 className="text-2xl font-bold text-white">{texts.title}</h2>
          <p className="text-sm text-slate-400 mt-2">{texts.desc}</p>
        </div>

        {authView === "login" && (
          <form onSubmit={handleLogin} className="space-y-4 relative z-10">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1 text-center">Uygulama Şifreniz</label>
              <input
                type="password"
                required
                value={loginPass}
                onChange={(e) => setLoginPass(e.target.value)}
                placeholder="••••••"
                className={`${inputBase} text-lg tracking-[0.3em]`}
              />
            </div>
            <button type="submit" className={submitBase}>
              <LogIn size={16} /> Giriş Yap
            </button>
            <button
              type="button"
              onClick={() => onSwitchView("forgot-1")}
              className="w-full text-slate-400 hover:text-brand-400 text-xs mt-3 transition-colors flex items-center justify-center gap-1"
            >
              Şifremi Unuttum <HelpCircle size={13} />
            </button>
          </form>
        )}

        {authView === "forgot-1" && (
          <form onSubmit={handleForgotStep1} className="space-y-4 relative z-10">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1 text-center">Kayıtlı Telefon Numaranız</label>
              <input
                type="tel"
                required
                value={forgotPhone}
                onChange={(e) => setForgotPhone(e.target.value)}
                placeholder="05XX XXX XX XX"
                className={`${inputBase} text-sm tracking-widest`}
              />
            </div>
            <button type="submit" className={submitBase}>
              Doğrulama Kodu Gönder <Send size={14} />
            </button>
            <button
              type="button"
              onClick={() => onSwitchView("login")}
              className="w-full text-slate-400 hover:text-white text-xs mt-2 transition-colors"
            >
              Girişe Dön
            </button>
          </form>
        )}

        {authView === "forgot-2" && (
          <form onSubmit={handleForgotStep2} className="space-y-4 relative z-10">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1 text-center">6 Haneli Doğrulama Kodu</label>
              <input
                type="number"
                required
                value={forgotOtp}
                onChange={(e) => setForgotOtp(e.target.value)}
                placeholder="Gelen Kodu Girin"
                className={`${inputBase} text-lg text-brand-400 tracking-[0.5em] font-mono`}
              />
            </div>
            <button type="submit" className={submitBase}>
              Kodu Doğrula
            </button>
            <button
              type="button"
              onClick={() => onSwitchView("forgot-1")}
              className="w-full text-slate-400 hover:text-white text-xs mt-2 transition-colors"
            >
              Geri Dön
            </button>
          </form>
        )}

        {authView === "forgot-3" && (
          <form onSubmit={handleForgotStep3} className="space-y-4 relative z-10">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1 text-center">Yeni Şifre Belirleyin</label>
              <input
                type="password"
                required
                value={forgotPass}
                onChange={(e) => setForgotPass(e.target.value)}
                className={`${inputBase} text-sm tracking-[0.3em]`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1 text-center">Şifreyi Tekrar Girin</label>
              <input
                type="password"
                required
                value={forgotPassConfirm}
                onChange={(e) => setForgotPassConfirm(e.target.value)}
                className={`${inputBase} text-sm tracking-[0.3em]`}
              />
            </div>
            <button type="submit" className={submitBase}>
              Şifreyi Yenile ve Giriş Yap
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
